import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, AtSign, UserPlus, MessageSquare, Check, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { initials, formatDate } from "@/lib/format";
import { toast } from "sonner";

const POLL_MS = 30000;

function iconFor(kind) {
  switch (kind) {
    case "mentioned": return <AtSign className="w-3.5 h-3.5 text-blue-600" />;
    case "assigned":  return <UserPlus className="w-3.5 h-3.5 text-emerald-600" />;
    case "comment":   return <MessageSquare className="w-3.5 h-3.5 text-violet-600" />;
    default:          return <Bell className="w-3.5 h-3.5 text-neutral-500" />;
  }
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [actors, setActors] = useState({});
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.notifications);
      setActors(Object.fromEntries(data.actors.map((a) => [a.id, a])));
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // also refresh whenever popover opens
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const markRead = async (n) => {
    if (!n.read) {
      try { await api.patch(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
  };

  const handleClick = async (n) => {
    await markRead(n);
    setOpen(false);
    if (n.list_id) navigate(`/list/${n.list_id}`);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
      toast.success("All caught up");
    } catch (e) {
      toast.error("Failed to mark all read");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-neutral-100 text-left transition-colors"
          data-testid="notifications-bell"
        >
          <Bell className="w-3.5 h-3.5" />
          <span className="text-sm">Notifications</span>
          {unread > 0 && (
            <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="notifications-unread-count">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" sideOffset={8} className="w-96 p-0">
        <div className="px-3 py-2.5 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Inbox</div>
            <div className="font-display text-base font-bold">Notifications</div>
          </div>
          {unread > 0 && (
            <button
              onClick={markAll}
              className="text-xs text-neutral-700 hover:text-neutral-900 flex items-center gap-1"
              data-testid="notifications-mark-all"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[480px] overflow-y-auto divide-y divide-neutral-100" data-testid="notifications-list">
          {items.length === 0 && (
            <div className="px-3 py-10 text-center text-xs text-neutral-500">No notifications yet.</div>
          )}
          {items.map((n) => {
            const actor = actors[n.actor_id];
            const title = n.data?.task_title || n.data?.title || "a task";
            const snippet = n.data?.snippet;
            return (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full px-3 py-2.5 text-left hover:bg-neutral-50 transition-colors flex items-start gap-2 ${n.read ? "" : "bg-blue-50/40"}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="w-6 h-6 rounded-full bg-neutral-950 text-white text-[9px] font-bold flex items-center justify-center shrink-0">
                  {initials(actor?.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-xs">
                    {iconFor(n.kind)}
                    <span className="font-semibold">{actor?.name || "Someone"}</span>
                    <span className="text-neutral-600">
                      {n.kind === "mentioned" && "mentioned you in"}
                      {n.kind === "assigned" && "assigned you to"}
                      {n.kind === "comment" && "commented on"}
                    </span>
                  </div>
                  <div className="text-sm font-medium truncate mt-0.5">{title}</div>
                  {snippet && (
                    <div className="text-xs text-neutral-500 truncate mt-0.5">"{snippet}"</div>
                  )}
                  <div className="font-mono-data text-[10px] text-neutral-400 mt-1">{formatDate(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
